import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DriveFileView } from '@project/contracts'
import { Button, Empty, Icon, Modal, Notice, Panel, Select, Table, type ColumnDef as TableColumn } from '../design/index.js'
import { useApi } from '../app/api.js'
import { ErrorBlock, Loading } from './feedback.js'
import { formatDate } from './format.js'
import { DropZone, UploadQueueList, useUploadQueue } from './UploadQueue.js'

const MAX_MB = 25

/**
 * The categories a site office actually files things under. Free text would
 * spell 'Drawings' four ways within a week, and the column is what the drive is
 * browsed by.
 */
const COLLECTIONS = [
  { value: '', label: 'Not filed under a collection' },
  { value: 'Drawings', label: 'Drawings' },
  { value: 'Test certificates', label: 'Test certificates' },
  { value: 'Photographs', label: 'Photographs' },
  { value: 'Invoices and bills', label: 'Invoices and bills' },
  { value: 'Correspondence', label: 'Correspondence' },
]

const sizeLabel = (bytes: number): string =>
  bytes < 1024 ? `${bytes} B`
  : bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB`
  : `${(bytes / 1024 / 1024).toFixed(1)} MB`

/** Reads a picked file into base64 without holding two copies of a big string. */
const toBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read'))
    reader.onload = () => {
      const result = String(reader.result)
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })

export const DriveTab = ({ docKey, docId }: { docKey: string; docId: string }) => {
  const api = useApi()
  const client = useQueryClient()
  const picker = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [collection, setCollection] = useState('')
  const [removing, setRemoving] = useState<DriveFileView | null>(null)

  const drive = useQuery({
    queryKey: ['drive', api.companyId, docKey, docId],
    queryFn: () => api.drive(docKey, docId),
  })

  const queue = useUploadQueue({
    maxBytes: MAX_MB * 1024 * 1024,
    onUploaded: () => { void client.invalidateQueries({ queryKey: ['drive', api.companyId, docKey, docId] }) },
  })

  /** Every file chosen or dropped together goes under the collection showing now. */
  const uploadAll = (files: readonly File[]): void => {
    setError(null)
    const into = collection
    queue.add(files, async (file) => api.driveUpload(docKey, docId, {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      base64: await toBase64(file),
      ...(into === '' ? {} : { collection: into }),
    }))
  }

  const remove = useMutation({
    mutationFn: (file: DriveFileView) => api.driveRemove(docKey, docId, file.id),
    onSuccess: () => {
      setError(null)
      setRemoving(null)
      void client.invalidateQueries({ queryKey: ['drive', api.companyId, docKey, docId] })
    },
    onError: (e: unknown) => {
      setRemoving(null)
      setError(e instanceof Error ? e.message : 'That file could not be removed')
    },
  })

  const rows = [...(drive.data ?? [])]

  const columns: TableColumn<DriveFileView>[] = [
    { key: 'fileName', header: 'File',
      render: (row) => (
        <a href={`/api/docs/${docKey}/${docId}/drive/${row.id}`} download={row.fileName}>{row.fileName}</a>
      ) },
    { key: 'collection', header: 'Collection', width: 200 },
    { key: 'byteSize', header: 'Size', width: 110, align: 'right',
      render: (row) => <span className="num">{sizeLabel(row.byteSize)}</span> },
    { key: 'uploadedByName', header: 'Uploaded by', width: 200 },
    { key: 'createdAt', header: 'Uploaded on', width: 150,
      render: (row) => <span className="num">{formatDate(row.createdAt.slice(0, 10))}</span> },
    { key: 'id', header: '', width: 110,
      render: (row) => (
        <Button kind="ghost" disabled={remove.isPending} onClick={() => setRemoving(row)}>Remove</Button>
      ) },
  ]

  return (
    <Panel
      title="Project drive"
      aside={rows.length > 0 ? `${rows.length} file${rows.length === 1 ? '' : 's'}` : undefined}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <input
          ref={picker}
          type="file"
          multiple
          aria-label="Files to add"
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = Array.from(e.currentTarget.files ?? [])
            e.currentTarget.value = ''
            uploadAll(files)
          }}
        />
        <Button kind="primary" icon={<Icon name="up" size={18} />}
          onClick={() => picker.current?.click()}>
          {queue.busy ? 'Uploading…' : 'Add files'}
        </Button>
        <div style={{ width: 260 }}>
          <Select
            value={collection}
            onChange={setCollection}
            options={COLLECTIONS}
            aria-label="File it under"
          />
        </div>
        <span style={{ font: '400 14px/1.5 var(--font-body)', color: 'var(--muted)' }}>
          Up to {MAX_MB} MB each. Pick several, or drop them below.
        </span>
      </div>

      {error ? <div style={{ marginBottom: 18 }}><Notice kind="bad">{error}</Notice></div> : null}

      <UploadQueueList items={queue.items} onClear={queue.clear} />

      <DropZone onFiles={uploadAll}>
        {drive.isLoading ? <Loading />
          : drive.error ? <ErrorBlock error={drive.error} />
          : rows.length === 0 ? (
            <Empty
              title="No files yet"
              body="Anything attached to this document is listed here — and stays with it, so the paperwork and the record do not drift apart. Drop files here, or use Add files."
            />
          ) : (
            <Table columns={columns} rows={rows} getKey={(r) => r.id} />
          )}
      </DropZone>

      {removing ? (
        <Modal
          title="Remove this file?"
          tone="warn"
          onClose={() => setRemoving(null)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setRemoving(null)}>Keep it</Button>
              <Button kind="primary" disabled={remove.isPending} onClick={() => remove.mutate(removing)}>
                Remove it
              </Button>
            </>
          }
        >
          <p>
            “{removing.fileName}” stops being listed against this document. The stored
            copy is kept, so nothing else pointing at the same file is affected.
          </p>
        </Modal>
      ) : null}
    </Panel>
  )
}
